import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { useAppDispatch } from "../../../app/hooks";
import { regenerateSysUrsTemplate } from "../ursTemplatesSlice";
import { AppId } from "../../../app/appTypes";

type RegenerateUrsDialogProps = {
    ursId: AppId;
    onClose: (regenerated?: boolean) => void;
    defaultTypeFilter?: string;
    defaultRequirementLanguage?: "PL" | "EN";
};

const REQUIREMENT_TYPES = [
    { value: "*", label: "Wszystkie typy" },
    { value: "URS-F", label: "URS-F — Wymagania funkcjonalne" },
    { value: "URS-S", label: "URS-S — Wymagania bezpieczeństwa" },
    { value: "URS-D", label: "URS-D — Wymagania projektowe" },
    { value: "URS-C", label: "URS-C — Wymagania dot. sterowania" },
    { value: "URS-A", label: "URS-A — Dodatkowe wymagania" },
    { value: "URS-U", label: "URS-U — Wymagania dot. mediów" },
    { value: "URS-R", label: "URS-R — Wymogi regulacyjne" },
    { value: "URS-DOC", label: "URS-DOC — Wymagania dot. dokumentacji" },
    { value: "URS-E", label: "URS-E — Wymagania środowiskowe" },
];

const REQUIREMENT_COUNT_STORAGE_KEY = "urs-regenerate-requirement-count";

const REQUIREMENT_COUNT_OPTIONS = [5, 10, 15, 20, 30, 50];

const RegenerateUrsDialog: React.FC<RegenerateUrsDialogProps> = ({
    ursId,
    onClose,
    defaultTypeFilter = "*",
    defaultRequirementLanguage = "PL",
}) => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [typeFilter, setTypeFilter] = useState<string>(defaultTypeFilter);

    const [requirementCount, setRequirementCount] = useState<number>(10);

    const [requirementLanguage, setRequirementLanguage] =
        useState<"PL" | "EN">(defaultRequirementLanguage);

    useEffect(() => {
        try {
            const stored = localStorage.getItem(REQUIREMENT_COUNT_STORAGE_KEY);
            if (stored) {
                const parsed = parseInt(stored, 10);
                if (!isNaN(parsed)) {
                    setRequirementCount(parsed);
                }
            }
        } catch {
        }
    }, []);

    const handleRequirementCountChange = (value: number) => {
        setRequirementCount(value);
        try {
            localStorage.setItem(
                REQUIREMENT_COUNT_STORAGE_KEY,
                value.toString()
            );
        } catch {
        }
    };

    const handleConfirm = async () => {
        setError(null);
        setLoading(true);
        try {
            await dispatch(
                regenerateSysUrsTemplate({
                    ursId,
                    typeFilter,
                    requirementCount,
                    requirementLanguage,
                })
            ).unwrap();

            setLoading(false);
            onClose(true);
        } catch (e: any) {
            setLoading(false);
            setError(
                e?.message ??
                e?.kind ??
                "Nie udało się zregenerować szablonu URS. Spróbuj ponownie."
            );
        }
    };

    return (
        <Dialog
            open={true}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Regeneruj szablon URS</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Czy na pewno chcesz wygenerować ponownie wymagania dla URS o ID{" "}
                    <b>{ursId}</b>? Obecne wymagania zostaną zastąpione nowymi
                    wygenerowanymi przez AI.
                </DialogContentText>

                {/* typ wymagań */}
                <FormControl fullWidth margin="dense">
                    <InputLabel id="urs-type-label">Typ wymagań</InputLabel>
                    <Select
                        labelId="urs-type-label"
                        label="Typ wymagań"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        disabled={loading}
                    >
                        {REQUIREMENT_TYPES.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                                {t.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* ile wymagań wygenerować (z localStorage) */}
                <FormControl fullWidth margin="dense">
                    <InputLabel id="requirement-count-label">
                        Ile wymagań wygenerować
                    </InputLabel>
                    <Select
                        labelId="requirement-count-label"
                        label="Ile wymagań wygenerować"
                        value={requirementCount}
                        onChange={(e) =>
                            handleRequirementCountChange(
                                Number(e.target.value)
                            )
                        }
                        disabled={loading}
                    >
                        {REQUIREMENT_COUNT_OPTIONS.map((n) => (
                            <MenuItem key={n} value={n}>
                                {n}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* język wymagań */}
                <FormControl fullWidth margin="dense">
                    <InputLabel id="requirement-lang-label">
                        Język wymagań
                    </InputLabel>
                    <Select
                        labelId="requirement-lang-label"
                        label="Język wymagań"
                        value={requirementLanguage}
                        onChange={(e) =>
                            setRequirementLanguage(
                                e.target.value as "PL" | "EN"
                            )
                        }
                        disabled={loading}
                    >
                        <MenuItem value="PL">Polski (PL)</MenuItem>
                        <MenuItem value="EN">Angielski (EN)</MenuItem>
                    </Select>
                </FormControl>

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)} disabled={loading}>
                    Anuluj
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : undefined}
                >
                    {loading ? "Generuję..." : "Regeneruj"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RegenerateUrsDialog;
